
import Vue from 'vue';
import { Component, InjectReactive, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { PackagesSelectionModal } from '@/lazy-load-components';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';
import { WebLocale } from '@/enums/web-locale';

import { ModalHelper } from '@/helpers/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import Provider from '@/interfaces/provider';

import { FilterCollection } from '@/stores/filter.store';
import { UserSettings } from '@/stores/user.store';
import { Genres } from '@/constants/types';
import { GenresByShortNameConstant } from '@/constants/ConstantsProvider.vue';
import { Package } from '@/@types/graphql-types';
import { RoutingState } from '@/stores/routing.store';

const Constant = namespace('constant');
const Filter = namespace('filter');
const User = namespace('user');

@Component({
	name: 'FilterBarPopularProvider',
	components: { HiddenHorizontalScrollbar, ProviderIcon, BasicButton, ResetButton },
})
export default class FilterBarPopularProvider extends Vue {
	_providersById: Record<number, Provider>;
	isModalOpening = false;

	@Prop({ default: true }) addButtonVisible: boolean;
	@Prop({ default: false }) showAddButtonFirst: boolean;
	@Prop() collectionType: CollectionType;
	@Prop() currentFilters: FilterCollection;
	@Prop({ type: Boolean, default: true }) providersClickable: boolean;
	@Prop({ default: () => [] }) userProviders: Provider[]; // used for serverCacheKey
	@Prop() userSettings: UserSettings; // used for serverCacheKey
	@Prop() webLocale: WebLocale; // used for serverCacheKey
	@Prop({ default: true }) updateRouteState: boolean; // when toggling providers
	@Prop({ default: true }) trackEvent: boolean; // when toggling providers
	@Prop() userSelectedPackages: string[]; // used for sports provider until we have a better flagging mechanism in place
	@Prop() provider: Provider;

	@Constant.Getter providers: () => Provider[];
	@Constant.Getter providersById: Record<number, Provider>;
	@Constant.Getter providersByShortName: Record<string, Package>;

	@InjectReactive(GenresByShortNameConstant) genresByShortName: Genres;

	@Filter.Action toggle: (payload: {
		filterName: keyof FilterCollection;
		value: any;
		selectedState: 1 | 0;
		updateRouteState?: boolean;
		userSelectedPackages?: string[];
		trackEvent?: boolean;
	}) => void;

	@User.State settings: UserSettings;

	get selectedProvider() {
		return this.userProviders.find(el => el.shortName === this.currentFilters.providers?.[0]);
	}

	get IMG_PROVIDERS_BAR_LOAD_COUNT() {
		return global.IMG_PROVIDERS_BAR_LOAD_COUNT;
	}

	get plusIcon() {
		return `/${ASSETS_DIR}/img/plus_icon.png`;
	}

	get plusIconIsVisible() {
		// causing issue with SSR > client hydration

		if (this.addButtonVisible) {
			return this.settings?.providers?.length > 0;
		}
		return this.addButtonVisible;
	}

	providerUrls = {} as Record<string, string>;

	// SSR perf: only call this.providersById once instead of every v-for loop.
	@Watch('userProviders', { immediate: true })
	onProvidersChange() {
		this._providersById = this.providersById;
	}

	@Watch('language', { immediate: true })
	async onLanguageChange() {
		// every call to a vuex getter seems to cost performance, that's why we only call it once before the loop
		const providersByShortName = this.providersByShortName;
		const genresByShortName = this.genresByShortName;
		const webLocale = this.webLocale;
		// cached getters don't work in SSR, that's why we optimize it by putting it into data

		this.providerUrls = this.userProviders.reduce((root, provider: Provider) => {
			root[provider.shortName] = calculateUrl(
				this.$router,
				undefined,
				this.collectionType,
				{
					...this.currentFilters,
					providers: [provider.shortName],
				},
				providersByShortName,
				genresByShortName
			);
			return root;
		}, {} as Record<string, string>);
	}

	providerClicked(providerName: string) {
		if (this.providersClickable) {
			const isActive: 1 | 0 = this.isFiltersContainsProvider(providerName) ? 0 : 1;
			const toggleData = {
				filterName: 'providers',
				value: providerName,
				selectedState: isActive,
				updateRouteState: this.updateRouteState,
				userSelectedPackages: this.userSelectedPackages,
				trackEvent: this.trackEvent,
			};

			this.toggle(toggleData as any);
			this.$emit('toggle', toggleData);
		}
	}
	isFiltersContainsProvider(providerName: string): boolean {
		return this.currentFilters.providers.includes(providerName);
	}
	isProviderActive(providerName: string): boolean {
		return this.currentFilters.providers.length === 0 || this.isFiltersContainsProvider(providerName);
	}

	async openProviderSelection() {
		this.isModalOpening = true;

		await ModalHelper.openModal(
			PackagesSelectionModal,
			{ providerPlace: ProviderFilterPlace.SETTINGS },
			{ cssClass: 'packages-selection-modal' },
			ProviderFilterPlace.SETTINGS,
			true
		);
		this.isModalOpening = false;
	}
}
